import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

export interface DailyChartRow {
  dayLabel: string;
  value: number;
}

interface StatisticsDailyLineChartProps {
  title: string;
  color: string;
  data: DailyChartRow[];
  isLoading?: boolean;
  /** Hebrew unit for tooltip label, e.g. "יום" or "שעה". Defaults to "יום". */
  xUnit?: string;
  /** Sum of `data[].value` for the selected range (shown under the chart). */
  rangeTotal?: number;
  /** Angle axis labels once there are more points than this. */
  denseFrom?: number;
}

export const StatisticsDailyLineChart: FC<StatisticsDailyLineChartProps> = ({
  title,
  color,
  data,
  isLoading,
  xUnit = "יום",
  rangeTotal,
  denseFrom = 14,
}) => {
  const denseAxis = data.length > denseFrom;
  const maxValue = data.length
    ? Math.max(...data.map((r) => r.value))
    : 0;
  const yAxisWidth = maxValue >= 1000 ? 56 : maxValue >= 100 ? 48 : 40;

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", minWidth: 0, py: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          טוען…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: "0.95rem",
          fontWeight: 600,
          color: "text.primary",
          textAlign: "center",
          mb: 1,
        }}
      >
        {title}
      </Typography>

      {!data.length ? (
        <Box
          sx={{
            py: 2,
            px: 1,
            borderRadius: 1,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            אין נתונים בטווח שנבחר
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: { xs: 240, sm: 280 },
            touchAction: "pan-y",
          }}
        >
          <ResponsiveContainer width="100%" height="100%" debounce={50}>
            <LineChart
              data={data}
              margin={{ top: 12, right: 20, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="4 4" />
              <XAxis
                dataKey="dayLabel"
                tick={{ fontSize: 11 }}
                interval={denseAxis ? "preserveStartEnd" : 0}
                padding={{ left: 8, right: 18 }}
                angle={denseAxis ? -35 : 0}
                textAnchor={denseAxis ? "end" : "middle"}
                height={denseAxis ? 64 : 36}
              />
              <YAxis
                allowDecimals={false}
                width={yAxisWidth}
                tickMargin={6}
                tick={{ fontSize: 11 }}
              />
              <RechartsTooltip
                formatter={(v: number) => [v, "כמות"]}
                labelFormatter={(label) => `${xUnit} ${label}`}
                contentStyle={{ direction: "rtl" }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2.5}
                dot={{ r: 4, fill: color }}
                activeDot={{ r: 6 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
      {typeof rangeTotal === "number" && data.length > 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", mt: 1, fontWeight: 600 }}
        >
          סה״כ בטווח: {rangeTotal.toLocaleString("he-IL")}
        </Typography>
      )}
    </Box>
  );
};
